import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ContentTemplate,
  CustomSelect,
  Divider,
  HeaderInput,
  SectionHeader,
  SingleSelectionRadio,
  Typography,
} from 'web-library';
import { useParams } from 'react-router-dom';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';

import { makeStyles } from 'tss-react/mui';
import {
  ExamLevelResponse,
  useGetExamRegistrationLevels,
} from '../../../api-refactored/unique-link/useGetExamRegistrationLevels';
import CandidateExamRegistrationList from './CandidateExamRegistrationList';
import CoordinatorGroupExamRegistrationList from './CoordinatorGroupExamRegistrationList';
import { useGetCheckAuthorityExist } from '../../../api-refactored/user/useGetCheckAuthorityExist';
import { useGetUserAuthorities } from '../../../api-refactored/user/useGetUserAuthorities';
import { GenericExamLevel } from '../../../api/candidate/exam/model';

const useStyles = makeStyles()((theme) => ({
  select: {
    marginBottom: theme.spacing(1),
  },
  formContainer: {},
  label: {
    width: '172px',
    textOverflow: 'ellipsis',
    color: theme.color.text.textHeader,
    marginRight: theme.spacing(2),
  },
  headerRow: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
  },
}));

const UniqueLinkAccountType = () => {
  const { nationalExamType } = useParams<{ nationalExamType: 'national' }>();

  const [selectedAccountType, setSelectedAccountType] = useState<string>('candidates');

  const [selectedExamType, setSelectedExamType] = useState<string | undefined>(undefined);
  const [examLevelError, setExamLevelError] = useState(false);
  const [isGCESelected, setIsGCESelected] = useState<boolean>(false);
  const [examTypeMap, setExamTypeMap] = useState<Map<string, string>>();
  const [selectedExamLevel, setSelectedExamLevel] = useState<GenericExamLevel>();

  const examLevelResponsesToMap = (res: ExamLevelResponse[]) => {
    const map = new Map<string, string>(res.map((r) => [r.uuid, r.code]));
    setExamTypeMap(map);
  };

  const { data: examTypeDropdown } = useGetExamRegistrationLevels({
    accountType: selectedAccountType.toUpperCase(),
    nationalType: nationalExamType,
    onSuccess: (res) => examLevelResponsesToMap(res),
  });

  useGetCheckAuthorityExist({
    authorityName: AUTHORITY_NAMES.CP_ADMIN_GRANT_WITHDRAWAL_UNIQUE_LINK_VIEW,
  });

  const { data = [] } = useGetUserAuthorities();

  const hasCreatePermission = data.includes(
    AUTHORITY_NAMES.CP_ADMIN_GRANT_WITHDRAWAL_UNIQUE_LINK_CREATE
  );

  useEffect(() => {
    setSelectedExamType(undefined);
  }, [nationalExamType, setSelectedExamType]);

  const { classes } = useStyles();

  const { t } = useTranslation();

  const onTypeChange = (currentType: string) => {
    setSelectedAccountType(currentType);
    setSelectedExamType('');
  };

  const onExamLevelChange = (examLevelUuid: string) => {
    setSelectedExamType(examLevelUuid);
    setExamLevelError(false);
    const examLevelCode = examTypeMap?.get(examLevelUuid);
    switch (examLevelCode) {
      case 'GCE':
        setSelectedExamLevel(GenericExamLevel.GCE);
        break;
      case 'PSLE':
        setSelectedExamLevel(GenericExamLevel.PSLE);
        break;
      default:
        setSelectedExamLevel(undefined);
        break;
    }
  };

  useEffect(() => {
    setIsGCESelected(
      examTypeDropdown?.some(
        (examType) => examType.code === 'GCE' && examType.uuid === selectedExamType
      ) || false
    );
  }, [examTypeDropdown, selectedExamType]);

  return (
    <>
      <ContentTemplate type="full">
        <SectionHeader title={t('admin->>UniqueLink.AccountDetails')} />
        <HeaderInput
          label={t('admin->>UniqueLink.AccountType')}
          input={
            <SingleSelectionRadio
              color="primary"
              value={selectedAccountType}
              onChange={(event) => {
                onTypeChange(event.target.value);
                setExamLevelError(false);
              }}
              data={[
                { label: t('admin->>UniqueLink.Candidate'), value: 'candidates' },
                { label: t('admin->>UniqueLink.Coordinator'), value: 'coordinators' },
              ]}
            />
          }
          topBottom
          alignCenter={false}
        />
        <HeaderInput
          label={t('admin->>UniqueLink.ExamLevel')}
          input={
            <>
              <CustomSelect
                id="inputSelect-examType"
                key={`inputSelect-examType-${nationalExamType}-${selectedAccountType}`}
                isHeader
                displayEmpty
                menuItems={
                  examTypeDropdown?.map((item) => ({
                    children: item.description,
                    key: item.uuid,
                    value: item.uuid,
                  })) || []
                }
                onChange={(event) => onExamLevelChange(event.target.value as string)}
                className={classes.select}
                placeholder={t('admin->>UniqueLink.ExamLevelPlaceholder')}
                labelId="inputSelectLabel-examType"
              />

              {examLevelError && (
                <Typography variant="body2" color="error">
                  {t('admin->>UniqueLink.NoExamLevelError')}
                </Typography>
              )}
            </>
          }
          topBottom
          alignCenter={false}
        />

        <Divider />

        {selectedAccountType === 'candidates' ? (
          <CandidateExamRegistrationList
            key={`${nationalExamType}-${selectedAccountType}-${selectedExamType}`}
            selectedExamType={selectedExamType}
            setExamLevelError={setExamLevelError}
            hasCreatePermission={hasCreatePermission}
            isGCESelected={isGCESelected}
          />
        ) : (
          <CoordinatorGroupExamRegistrationList
            key={`${nationalExamType}-${selectedAccountType}-${selectedExamType}`}
            selectedExamType={selectedExamType}
            setExamLevelError={setExamLevelError}
            nationalExamType={nationalExamType}
            hasCreatePermission={hasCreatePermission}
            isGCESelected={isGCESelected}
            selectedExamLevel={selectedExamLevel}
          />
        )}
      </ContentTemplate>
    </>
  );
};
export default UniqueLinkAccountType;
